import React, { useState, useEffect, useCallback } from 'react';

const BrainAdminTab = ({ API, adminToken, isDarkMode, showNotification }) => {
  // Section toggle
  const [activeSection, setActiveSection] = useState('log');

  // Brain Log state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);

  // Missing Words state
  const [missingWords, setMissingWords] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);

  // Corrections state
  const [corrections, setCorrections] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);

  // Brain Rules state
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSummary, setRulesSummary] = useState({});
  const [rulesSubTab, setRulesSubTab] = useState('conjugation');
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState(null);

  // Test & Correct state
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testBreakdown, setTestBreakdown] = useState([]); // breakdown from analyze
  const [testBreakdownEdits, setTestBreakdownEdits] = useState([]); // editable copy
  const [savingCorrection, setSavingCorrection] = useState(false);

  // Source filter for logs
  const [logSourceFilter, setLogSourceFilter] = useState('all'); // 'all', 'user', 'admin'

  // Expanded log row
  const [expandedLog, setExpandedLog] = useState(null);

  // Breakdown state for expanded log row
  const [breakdownEdits, setBreakdownEdits] = useState([]); // [{en, pos, latin, arabic}]
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  // ─── API Calls ──────────────────────────────────────────────────────────────

  const fetchLogs = useCallback(async (page = 1) => {
    setLogsLoading(true);
    try {
      const res = await fetch(`${API}/admin/brain/logs?page=${page}&limit=50`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        console.log('[BRAIN TAB] Logs received:', data.logs?.length, 'First:', data.logs?.[0]);
        setLogs(data.logs || []);
        setLogsTotal(data.total || 0);
        setLogsPage(page);
      } else {
        console.error('[BRAIN TAB] Logs fetch failed:', res.status, await res.text());
      }
    } catch (err) {
      console.error('Error fetching brain logs:', err);
    }
    setLogsLoading(false);
  }, [API, adminToken]);

  const fetchMissingWords = useCallback(async () => {
    setMissingLoading(true);
    try {
      const res = await fetch(`${API}/admin/brain/missing-words`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMissingWords(data.words || []);
      }
    } catch (err) {
      console.error('Error fetching missing words:', err);
    }
    setMissingLoading(false);
  }, [API, adminToken]);

  const fetchCorrections = useCallback(async () => {
    setCorrectionsLoading(true);
    try {
      const res = await fetch(`${API}/admin/brain/corrections`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCorrections(data.corrections || []);
      }
    } catch (err) {
      console.error('Error fetching corrections:', err);
    }
    setCorrectionsLoading(false);
  }, [API, adminToken]);

  const deleteLog = async (logId) => {
    try {
      const res = await fetch(`${API}/admin/brain/logs/${logId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification && showNotification('Log deleted', 'success');
        fetchLogs(logsPage);
        fetchMissingWords();
      }
    } catch (err) {
      console.error('Error deleting log:', err);
    }
  };

  // Fetch breakdown from analyze endpoint (for old logs or test)
  const fetchBreakdown = async (text) => {
    try {
      const res = await fetch(`${API}/admin/brain/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({ text })
      });
      if (res.ok) {
        const data = await res.json();
        return data.breakdown || [];
      }
    } catch (err) {
      console.error('Error fetching breakdown:', err);
    }
    return [];
  };

  // Expand a log row — loads breakdown (from DB or live analyze)
  const expandLogRow = async (idx, log) => {
    if (expandedLog === idx) {
      setExpandedLog(null);
      setBreakdownEdits([]);
      return;
    }
    setExpandedLog(idx);
    if (log.breakdown && log.breakdown.length > 0) {
      setBreakdownEdits(log.breakdown.map(b => ({ ...b })));
    } else {
      // Old log — fetch live breakdown
      setBreakdownLoading(true);
      setBreakdownEdits([]);
      const bd = await fetchBreakdown(log.input || log.english_input || '');
      setBreakdownEdits(bd.map(b => ({ ...b })));
      setBreakdownLoading(false);
    }
  };

  const updateBreakdownBox = (idx, field, value) => {
    setBreakdownEdits(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  };

  const updateTestBreakdownBox = (idx, field, value) => {
    setTestBreakdownEdits(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  };

  // Move a row up or down in breakdown edits (for reordering words)
  const moveBreakdownRow = (idx, direction) => {
    setBreakdownEdits(prev => {
      const arr = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const moveTestBreakdownRow = (idx, direction) => {
    setTestBreakdownEdits(prev => {
      const arr = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  // Save correction from breakdown edits. logId is optional — if provided, marks the log as corrected.
  const saveBreakdownCorrection = async (inputText, edits, logId) => {
    const newLatin = edits.map(b => b.latin).filter(Boolean).join(' ');
    const newArabic = edits.map(b => b.arabic).filter(Boolean).join(' ');
    if (!newLatin.trim()) {
      showNotification && showNotification('Latin cannot be empty', 'error');
      return;
    }
    setSavingCorrection(true);
    try {
      const res = await fetch(`${API}/admin/brain/corrections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          input_text: inputText,
          correct_latin: newLatin.trim(),
          correct_arabic: newArabic.trim()
        })
      });
      if (res.ok) {
        showNotification && showNotification('Correction saved!', 'success');
        fetchCorrections();
        fetchMissingWords();
        // Find matching log if no logId (Test & Correct creates log via /translate)
        if (!logId) {
          const matchingLog = logs.find(l => (l.input || l.english_input || '') === inputText);
          if (matchingLog) logId = matchingLog.id;
        }
        // Mark the log as corrected + update output with corrected text
        if (logId) {
          await fetch(`${API}/admin/brain/logs/${logId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
            body: JSON.stringify({
              corrected: true,
              output_latin: newLatin.trim(),
              output_arabic: newArabic.trim(),
              breakdown: edits
            })
          });
        }
        // Refresh logs AFTER the PUT completes so corrected flag is included
        fetchLogs(logsPage);
      } else {
        showNotification && showNotification('Failed to save correction', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error: ' + err.message, 'error');
    }
    setSavingCorrection(false);
  };

  const runBrainTest = async () => {
    if (!testInput.trim()) return;
    setTestLoading(true);
    setTestResult(null);
    setTestBreakdown([]);
    setTestBreakdownEdits([]);
    try {
      // Run translate + analyze in parallel
      const [translateRes, analyzeRes] = await Promise.all([
        fetch(`${API}/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: testInput, source_language: 'en', source: 'admin' })
        }),
        fetch(`${API}/admin/brain/analyze`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify({ text: testInput })
        })
      ]);
      if (translateRes.ok) {
        const data = await translateRes.json();
        setTestResult(data);
      } else {
        const err = await translateRes.json().catch(() => ({}));
        setTestResult({ error: err.detail || 'Translation failed' });
      }
      if (analyzeRes.ok) {
        const analyzeData = await analyzeRes.json();
        const bd = analyzeData.breakdown || [];
        setTestBreakdown(bd);
        setTestBreakdownEdits(bd.map(b => ({ ...b })));
      }
    } catch (err) {
      setTestResult({ error: err.message });
    }
    setTestLoading(false);
  };

  const saveTestCorrection = async () => {
    await saveBreakdownCorrection(testInput.trim(), testBreakdownEdits);
  };

  const deleteCorrection = async (id) => {
    try {
      const res = await fetch(`${API}/admin/brain/corrections/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification && showNotification('Correction deleted', 'success');
        fetchCorrections();
      }
    } catch (err) {
      console.error('Error deleting correction:', err);
    }
  };

  // ─── Brain Rules API ──────────────────────────────────────────────────────

  const fetchRules = useCallback(async (type) => {
    setRulesLoading(true);
    try {
      const url = type ? `${API}/admin/brain/rules?rule_type=${type}` : `${API}/admin/brain/rules`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRules(data.rules || []);
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
    }
    setRulesLoading(false);
  }, [API, adminToken]);

  const fetchRulesSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/brain/rules/summary`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRulesSummary(data.summary || {});
      }
    } catch (err) {
      console.error('Error fetching rules summary:', err);
    }
  }, [API, adminToken]);

  const saveRule = async (rule) => {
    try {
      const isNew = !rule.id;
      const url = isNew ? `${API}/admin/brain/rules` : `${API}/admin/brain/rules/${rule.id}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminToken}` },
        body: JSON.stringify(rule)
      });
      if (res.ok) {
        showNotification && showNotification(isNew ? 'Rule created' : 'Rule updated', 'success');
        setEditingRule(null);
        setNewRule(null);
        fetchRules(rulesSubTab);
        fetchRulesSummary();
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification && showNotification(err.detail || 'Failed to save rule', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error: ' + err.message, 'error');
    }
  };

  const deleteRule = async (ruleId) => {
    try {
      const res = await fetch(`${API}/admin/brain/rules/${ruleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        showNotification && showNotification('Rule deleted', 'success');
        fetchRules(rulesSubTab);
        fetchRulesSummary();
      }
    } catch (err) {
      console.error('Error deleting rule:', err);
    }
  };

  const [fillingArabic, setFillingArabic] = useState(false);

  const fillArabic = async () => {
    setFillingArabic(true);
    try {
      const res = await fetch(`${API}/admin/brain/rules/fill-arabic`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        showNotification && showNotification(`Filled Arabic for ${data.filled} rules`, 'success');
        fetchRules(rulesSubTab);
        fetchRulesSummary();
      } else {
        showNotification && showNotification('Fill Arabic failed', 'error');
      }
    } catch (err) {
      showNotification && showNotification('Error: ' + err.message, 'error');
    }
    setFillingArabic(false);
  };

  // ─── Load data on section change ────────────────────────────────────────────

  useEffect(() => {
    if (activeSection === 'log') {
      fetchLogs(1);
      fetchMissingWords();
    } else if (activeSection === 'teach') {
      fetchCorrections();
    } else if (activeSection === 'rules') {
      fetchRules(rulesSubTab);
      fetchRulesSummary();
    }
  }, [activeSection, fetchLogs, fetchMissingWords, fetchCorrections, fetchRules, fetchRulesSummary, rulesSubTab]);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const confidenceColor = (c) => {
    if (c > 0.8) return isDarkMode ? 'text-green-400 bg-green-900/30' : 'text-green-700 bg-green-100';
    if (c >= 0.5) return isDarkMode ? 'text-yellow-400 bg-yellow-900/30' : 'text-yellow-700 bg-yellow-100';
    return isDarkMode ? 'text-red-400 bg-red-900/30' : 'text-red-700 bg-red-100';
  };

  const posColor = (pos) => {
    const colors = {
      'PRONOUN': 'bg-blue-100 text-blue-700',
      'VERB': 'bg-purple-100 text-purple-700',
      'NOUN': 'bg-orange-100 text-orange-700',
      'ADJ': 'bg-pink-100 text-pink-700',
      'MODAL': 'bg-indigo-100 text-indigo-700',
      'PREP': 'bg-gray-100 text-gray-600',
      'ADVERB_BEFORE': 'bg-teal-100 text-teal-700',
      'ADVERB_AFTER': 'bg-teal-100 text-teal-700',
      'CONNECTOR': 'bg-yellow-100 text-yellow-700',
      'HAVE': 'bg-emerald-100 text-emerald-700',
      'COPULA': 'bg-gray-200 text-gray-500',
      'ARTICLE': 'bg-gray-200 text-gray-400',
      'TO': 'bg-gray-200 text-gray-500',
      'WH': 'bg-cyan-100 text-cyan-700',
      'NEG': 'bg-red-100 text-red-700',
      'DEM': 'bg-amber-100 text-amber-700',
      'QUANT': 'bg-lime-100 text-lime-700',
      'CORRECTION': 'bg-emerald-100 text-emerald-700',
    };
    if (isDarkMode) {
      const darkColors = {
        'PRONOUN': 'bg-blue-900/40 text-blue-300',
        'VERB': 'bg-purple-900/40 text-purple-300',
        'NOUN': 'bg-orange-900/40 text-orange-300',
        'ADJ': 'bg-pink-900/40 text-pink-300',
        'MODAL': 'bg-indigo-900/40 text-indigo-300',
        'HAVE': 'bg-emerald-900/40 text-emerald-300',
        'WH': 'bg-cyan-900/40 text-cyan-300',
        'NEG': 'bg-red-900/40 text-red-300',
      };
      return darkColors[pos] || 'bg-gray-700 text-gray-300';
    }
    return colors[pos] || 'bg-gray-100 text-gray-600';
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return ts; }
  };

  const filteredLogs = logSourceFilter === 'all' ? logs : logs.filter(l => (l.source || 'user') === logSourceFilter);
  const totalPages = Math.ceil(logsTotal / 50);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Section Toggle */}
      <div className="flex gap-2">
        {[
          { id: 'log', label: 'Brain Log', icon: '📊' },
          { id: 'teach', label: 'Brain Teach', icon: '🎓' },
          { id: 'rules', label: 'Brain Rules', icon: '⚙️' }
        ].map(s => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeSection === s.id
                ? 'bg-emerald-600 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {/* ═══ BRAIN LOG SECTION ═══ */}
      {activeSection === 'log' && (
        <div className="space-y-4">
          {/* Header + Refresh */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Brain Activity Log
              </h3>
              <div className="flex gap-2 items-center">
                <select
                  value={logSourceFilter}
                  onChange={e => setLogSourceFilter(e.target.value)}
                  className={`px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                >
                  <option value="all">All Sources</option>
                  <option value="user">User Only</option>
                  <option value="admin">Admin Only</option>
                </select>
                <span className={`text-sm px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {filteredLogs.length}{logSourceFilter !== 'all' ? `/${logsTotal}` : ''} logs
                </span>
                <button
                  onClick={() => { fetchLogs(logsPage); fetchMissingWords(); }}
                  disabled={logsLoading}
                  className="px-3 py-1 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  {logsLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>

            {/* Log Table */}
            {logs.length === 0 && !logsLoading ? (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No brain logs yet. Translate a sentence that's not in the DB to see the brain work.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                      <th className="text-left p-2">English</th>
                      <th className="text-left p-2">Latin</th>
                      <th className="text-left p-2">Arabic</th>
                      <th className="text-center p-2">Confidence</th>
                      <th className="text-center p-2">Case</th>
                      <th className="text-center p-2">Missing</th>
                      <th className="text-center p-2">Source</th>
                      <th className="text-right p-2">Time</th>
                      <th className="text-right p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, idx) => (
                      <React.Fragment key={log.id || idx}>
                        <tr
                          className={`cursor-pointer hover:${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} ${
                            isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'
                          }`}
                          onClick={() => expandLogRow(idx, log)}
                        >
                          <td className={`p-2 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {log.input || log.english_input || '(no english)'}
                          </td>
                          <td className={`p-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                            {log.output_latin}
                          </td>
                          <td className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} dir="rtl">
                            {log.output_arabic || '-'}
                          </td>
                          <td className="p-2 text-center">
                            {log.corrected ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${isDarkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
                                Fixed
                              </span>
                            ) : (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${confidenceColor(log.confidence)}`}>
                                {(log.confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </td>
                          <td className={`p-2 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {log.case_used}
                          </td>
                          <td className="p-2 text-center">
                            {log.missing_words && log.missing_words.length > 0 ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'}`}>
                                {log.missing_words.length}
                              </span>
                            ) : (
                              <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>-</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              (log.source || 'user') === 'admin'
                                ? (isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700')
                                : (isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700')
                            }`}>
                              {(log.source || 'user') === 'admin' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td className={`p-2 text-right text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatTime(log.timestamp)}
                          </td>
                          <td className="p-2 text-right">
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteLog(log.id); }}
                              className={`px-2 py-0.5 rounded text-xs ${isDarkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-800/60' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                            >Del</button>
                          </td>
                        </tr>
                        {/* Expanded: Brain Analysis + Correction */}
                        {expandedLog === idx && (
                          <tr className={isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                            <td colSpan={8} className="p-4" onClick={e => e.stopPropagation()}>

                              {breakdownLoading ? (
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Analyzing...</p>
                              ) : breakdownEdits.length === 0 ? (
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No breakdown available</p>
                              ) : (
                                <>
                                  {/* ── Section 1: How Brain Translated (read-only) ── */}
                                  <div className={`mb-4 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
                                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                                      How Brain Translated:
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1">
                                      {breakdownEdits.map((box, bi) => (
                                        <div key={bi} className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
                                          box.missing
                                            ? (isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300')
                                            : (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-blue-200')
                                        } border`}>
                                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{box.en}</span>
                                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>&rarr;</span>
                                          {box.missing ? (
                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>???</span>
                                          ) : (
                                            <span className={`text-xs font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{box.latin}</span>
                                          )}
                                          <span className={`text-[10px] px-1 rounded ${posColor(box.pos)}`}>{box.pos}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {log.missing_words && log.missing_words.length > 0 && (
                                      <div className="mt-2 flex items-center gap-1">
                                        <span className={`text-xs font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Missing:</span>
                                        {log.missing_words.map((w, wi) => (
                                          <span key={wi} className={`px-1.5 py-0.5 rounded text-xs ${isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700'}`}>{w}</span>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* ── Section 2: Correction Table (editable) ── */}
                                  <div className={`p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-yellow-700 bg-gray-800' : 'border-yellow-300 bg-yellow-50'}`}>
                                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                                      Correction:
                                    </div>
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr>
                                          <th className={`text-left p-1 text-xs w-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}></th>
                                          <th className={`text-left p-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>English</th>
                                          <th className={`text-left p-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Latin</th>
                                          <th className={`text-left p-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Arabic</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {breakdownEdits.map((box, bi) => (
                                          <tr key={bi} className={box.missing ? (isDarkMode ? 'bg-red-900/20' : 'bg-red-50') : ''}>
                                            <td className="p-1 text-center">
                                              <div className="flex flex-col gap-0.5">
                                                <button
                                                  onClick={() => moveBreakdownRow(bi, -1)}
                                                  disabled={bi === 0}
                                                  className={`text-xs leading-none px-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-600' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'} disabled:opacity-20`}
                                                  title="Move up"
                                                >▲</button>
                                                <button
                                                  onClick={() => moveBreakdownRow(bi, 1)}
                                                  disabled={bi === breakdownEdits.length - 1}
                                                  className={`text-xs leading-none px-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-600' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'} disabled:opacity-20`}
                                                  title="Move down"
                                                >▼</button>
                                              </div>
                                            </td>
                                            <td className={`p-1 font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                              {box.en}
                                              <span className={`ml-1 text-[10px] px-1 rounded ${posColor(box.pos)}`}>{box.pos}</span>
                                              {box.missing && <span className={`ml-1 text-[10px] px-1 rounded font-bold ${isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-600'}`}>MISSING</span>}
                                            </td>
                                            <td className="p-1">
                                              <input
                                                type="text"
                                                value={box.latin}
                                                onChange={e => updateBreakdownBox(bi, 'latin', e.target.value)}
                                                placeholder={box.missing ? 'Add translation...' : ''}
                                                className={`w-full px-2 py-1 rounded border text-sm ${box.missing ? (isDarkMode ? 'bg-gray-700 border-red-600 text-emerald-400' : 'bg-white border-red-400 text-emerald-700') : (isDarkMode ? 'bg-gray-700 border-gray-600 text-emerald-400' : 'bg-white border-gray-300 text-emerald-700')}`}
                                              />
                                            </td>
                                            <td className="p-1">
                                              <input
                                                type="text"
                                                value={box.arabic}
                                                onChange={e => updateBreakdownBox(bi, 'arabic', e.target.value)}
                                                dir="rtl"
                                                placeholder={box.missing ? '...' : ''}
                                                className={`w-full px-2 py-1 rounded border text-sm ${box.missing ? (isDarkMode ? 'bg-gray-700 border-red-600 text-gray-300' : 'bg-white border-red-400 text-gray-600') : (isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-600')}`}
                                              />
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    <div className="mt-3">
                                      <button
                                        onClick={() => saveBreakdownCorrection(log.input || log.english_input || '', breakdownEdits, log.id)}
                                        disabled={savingCorrection}
                                        className="px-4 py-1.5 rounded bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 disabled:opacity-50"
                                      >
                                        {savingCorrection ? 'Saving...' : 'Save Correction'}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              )}

                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                <button
                  onClick={() => fetchLogs(logsPage - 1)}
                  disabled={logsPage <= 1}
                  className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'} disabled:opacity-30`}
                >
                  Prev
                </button>
                <span className={`px-3 py-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Page {logsPage} of {totalPages}
                </span>
                <button
                  onClick={() => fetchLogs(logsPage + 1)}
                  disabled={logsPage >= totalPages}
                  className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'} disabled:opacity-30`}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Missing Words Summary */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Missing Words
            </h3>
            {missingLoading ? (
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading...</p>
            ) : missingWords.length === 0 ? (
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No missing words yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {missingWords.map((mw, i) => (
                  <span key={i} className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-red-900/20 text-red-400 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {mw.word} <span className="font-bold">x{mw.count}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ BRAIN TEACH SECTION ═══ */}
      {activeSection === 'teach' && (
        <div className="space-y-4">
          {/* Test & Correct */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Test & Correct Brain
            </h3>

            {/* Input */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={testInput}
                onChange={e => setTestInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runBrainTest()}
                placeholder="Type English sentence..."
                className={`flex-1 px-3 py-2 rounded border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400'
                }`}
              />
              <button
                onClick={runBrainTest}
                disabled={testLoading || !testInput.trim()}
                className="px-4 py-2 rounded bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {testLoading ? 'Running...' : 'Run Brain'}
              </button>
            </div>

            {/* Brain Result — error */}
            {testResult && testResult.error && (
              <div className={`p-3 rounded mb-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="text-red-500">{testResult.error}</p>
              </div>
            )}

            {/* Brain Result + Breakdown */}
            {testResult && !testResult.error && (
              <div className="space-y-4">
                {/* Result summary */}
                <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className={`mb-2 text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {testInput}
                  </div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`text-sm font-bold ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Result:</span>
                    <span className={`font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{testResult.latinScript}</span>
                    {testResult.translatedText && <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'} dir="rtl">{testResult.translatedText}</span>}
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${confidenceColor(testResult.confidence || 0)}`}>
                      {((testResult.confidence || 0) * 100).toFixed(0)}%
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {testResult.apiSource || ''}
                    </span>
                  </div>
                </div>

                {/* How Brain Translated (read-only) */}
                {testBreakdown.length > 0 && (
                  <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'}`}>
                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>
                      How Brain Translated:
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      {testBreakdown.map((box, bi) => (
                        <div key={bi} className={`inline-flex items-center gap-1 px-2 py-1 rounded ${box.missing ? (isDarkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300') : (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-blue-200')} border`}>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{box.en}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>&rarr;</span>
                          {box.missing ? (
                            <span className="text-xs font-bold text-red-400">???</span>
                          ) : (
                            <span className={`text-xs font-bold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{box.latin}</span>
                          )}
                          <span className={`text-[10px] px-1 rounded ${posColor(box.pos)}`}>{box.pos}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Correction Table (editable) */}
                {testBreakdownEdits.length > 0 && (
                  <div className={`p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-yellow-700 bg-gray-800' : 'border-yellow-300 bg-yellow-50'}`}>
                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                      Correction:
                    </div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className={`text-left p-1 text-xs w-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}></th>
                          <th className={`text-left p-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>English</th>
                          <th className={`text-left p-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Latin</th>
                          <th className={`text-left p-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Arabic</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testBreakdownEdits.map((box, bi) => (
                          <tr key={bi} className={box.missing ? (isDarkMode ? 'bg-red-900/20' : 'bg-red-50') : ''}>
                            <td className="p-1 text-center">
                              <div className="flex flex-col gap-0.5">
                                <button
                                  onClick={() => moveTestBreakdownRow(bi, -1)}
                                  disabled={bi === 0}
                                  className={`text-xs leading-none px-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-600' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'} disabled:opacity-20`}
                                  title="Move up"
                                >▲</button>
                                <button
                                  onClick={() => moveTestBreakdownRow(bi, 1)}
                                  disabled={bi === testBreakdownEdits.length - 1}
                                  className={`text-xs leading-none px-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-600' : 'text-gray-400 hover:text-gray-800 hover:bg-gray-200'} disabled:opacity-20`}
                                  title="Move down"
                                >▼</button>
                              </div>
                            </td>
                            <td className={`p-1 font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              {box.en}
                              <span className={`ml-1 text-[10px] px-1 rounded ${posColor(box.pos)}`}>{box.pos}</span>
                              {box.missing && <span className={`ml-1 text-[10px] px-1 rounded font-bold ${isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-600'}`}>MISSING</span>}
                            </td>
                            <td className="p-1">
                              <input
                                type="text"
                                value={box.latin}
                                onChange={e => updateTestBreakdownBox(bi, 'latin', e.target.value)}
                                placeholder={box.missing ? 'Add translation...' : ''}
                                className={`w-full px-2 py-1 rounded border text-sm ${box.missing ? (isDarkMode ? 'bg-gray-700 border-red-600 text-emerald-400' : 'bg-white border-red-400 text-emerald-700') : (isDarkMode ? 'bg-gray-700 border-gray-600 text-emerald-400' : 'bg-white border-gray-300 text-emerald-700')}`}
                              />
                            </td>
                            <td className="p-1">
                              <input
                                type="text"
                                value={box.arabic}
                                onChange={e => updateTestBreakdownBox(bi, 'arabic', e.target.value)}
                                dir="rtl"
                                placeholder={box.missing ? '...' : ''}
                                className={`w-full px-2 py-1 rounded border text-sm ${box.missing ? (isDarkMode ? 'bg-gray-700 border-red-600 text-gray-300' : 'bg-white border-red-400 text-gray-600') : (isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-600')}`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-3">
                      <button
                        onClick={saveTestCorrection}
                        disabled={savingCorrection}
                        className="px-4 py-1.5 rounded bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {savingCorrection ? 'Saving...' : 'Save Correction'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Corrections List */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Saved Corrections ({corrections.length})
            </h3>
            {correctionsLoading ? (
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading...</p>
            ) : corrections.length === 0 ? (
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No corrections yet. Use "Test & Correct" above to teach the brain.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                      <th className="text-left p-2">English Input</th>
                      <th className="text-left p-2">Correct Latin</th>
                      <th className="text-left p-2">Correct Arabic</th>
                      <th className="text-right p-2">Date</th>
                      <th className="text-right p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {corrections.map((c, idx) => (
                      <tr key={c.id || idx} className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}>
                        <td className={`p-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {c.input_text}
                          <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${isDarkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>Fixed</span>
                        </td>
                        <td className={`p-2 font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{c.correct_latin}</td>
                        <td className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} dir="rtl">{c.correct_arabic || '-'}</td>
                        <td className={`p-2 text-right text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatTime(c.created_at || c.updated_at)}
                        </td>
                        <td className="p-2 text-right">
                          <button
                            onClick={() => deleteCorrection(c.id)}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ═══ BRAIN RULES SECTION ═══ */}
      {activeSection === 'rules' && (
        <div className="space-y-4">
          {/* Header + Seed */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Brain Rules Engine
              </h3>
              <div className="flex gap-2 items-center">
                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {Object.entries(rulesSummary).map(([k, v]) => `${k}: ${v}`).join(' | ') || 'No rules yet'}
                </span>
                <button
                  onClick={fillArabic}
                  disabled={fillingArabic}
                  className="px-3 py-1.5 rounded text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {fillingArabic ? 'Filling...' : 'Fill Arabic from DB'}
                </button>
                <button
                  onClick={() => { fetchRules(rulesSubTab); fetchRulesSummary(); }}
                  className={`px-3 py-1.5 rounded text-xs font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 flex-wrap">
              {[
                { id: 'conjugation', label: 'Conjugation' },
                { id: 'verb_stem', label: 'Verb Stems' },
                { id: 'context_rule', label: 'Context Rules' },
                { id: 'word_order', label: 'Word Order' },
                { id: 'sentence_pattern', label: 'Sentence Patterns' },
                { id: 'place_word', label: 'Place Words' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setRulesSubTab(tab.id); setEditingRule(null); setNewRule(null); }}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    rulesSubTab === tab.id
                      ? 'bg-emerald-600 text-white'
                      : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label} {rulesSummary[tab.id] ? `(${rulesSummary[tab.id]})` : ''}
                </button>
              ))}
            </div>
          </div>

          {/* Rules Table */}
          <div className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {rulesSubTab === 'conjugation' && 'Conjugation Patterns'}
                {rulesSubTab === 'verb_stem' && 'Verb Stems'}
                {rulesSubTab === 'context_rule' && 'Context Rules'}
                {rulesSubTab === 'word_order' && 'Word Order Rules'}
                {rulesSubTab === 'sentence_pattern' && 'Sentence Patterns'}
                {rulesSubTab === 'place_word' && 'Place Words'}
              </h4>
              <button
                onClick={() => {
                  const templates = {
                    conjugation: { type: 'conjugation', tense: 'past', slot: '', prefix: '', ending: '', prefixAr: '', endingAr: '' },
                    verb_stem: { type: 'verb_stem', english: '', pastStem: '', pastStemAr: '', presentStem: '', presentStemAr: '', phrasal: false },
                    context_rule: { type: 'context_rule', verb: '', condition: 'next_is_place', use_verb: '', add_word: '', add_word_ar: '' },
                    word_order: { type: 'word_order', pattern: '', action: 'flip', description: '' },
                    sentence_pattern: { type: 'sentence_pattern', mode: '', pattern: '', patternAr: '' },
                    place_word: { type: 'place_word', english: '', darija: '', darijaAr: '' },
                  };
                  setNewRule(templates[rulesSubTab] || { type: rulesSubTab });
                  setEditingRule(null);
                }}
                className="px-3 py-1.5 rounded text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
              >
                + Add Rule
              </button>
            </div>

            {rulesLoading ? (
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading...</p>
            ) : (
              <>
                {/* New Rule Form */}
                {newRule && (
                  <div className={`mb-4 p-3 rounded-lg border-2 border-dashed ${isDarkMode ? 'border-emerald-700 bg-gray-700' : 'border-emerald-300 bg-emerald-50'}`}>
                    <div className={`text-xs font-bold mb-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>New Rule</div>
                    <RuleForm rule={newRule} onChange={setNewRule} isDarkMode={isDarkMode} ruleType={rulesSubTab} />
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => saveRule(newRule)} className="px-3 py-1 rounded text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700">Save</button>
                      <button onClick={() => setNewRule(null)} className={`px-3 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Rules List */}
                {rules.length === 0 ? (
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No {rulesSubTab} rules yet. Click "+ Add Rule" to create manually.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}>
                          {rulesSubTab === 'conjugation' && <><th className="text-left p-2">Tense</th><th className="text-left p-2">Slot</th><th className="text-left p-2">Prefix</th><th className="text-left p-2">Ending</th><th className="text-left p-2">Prefix (Ar)</th><th className="text-left p-2">Ending (Ar)</th></>}
                          {rulesSubTab === 'verb_stem' && <><th className="text-left p-2">English</th><th className="text-left p-2">Past Stem</th><th className="text-left p-2">Past (Ar)</th><th className="text-left p-2">Present Stem</th><th className="text-left p-2">Present (Ar)</th><th className="text-left p-2">Phrasal</th></>}
                          {rulesSubTab === 'context_rule' && <><th className="text-left p-2">Verb</th><th className="text-left p-2">Condition</th><th className="text-left p-2">Use Verb</th><th className="text-left p-2">Add Word</th><th className="text-left p-2">Add Word (Ar)</th></>}
                          {rulesSubTab === 'word_order' && <><th className="text-left p-2">Pattern</th><th className="text-left p-2">Action</th><th className="text-left p-2">Description</th></>}
                          {rulesSubTab === 'sentence_pattern' && <><th className="text-left p-2">Mode</th><th className="text-left p-2">Pattern</th><th className="text-left p-2">Pattern (Ar)</th></>}
                          {rulesSubTab === 'place_word' && <><th className="text-left p-2">English</th><th className="text-left p-2">Darija</th><th className="text-left p-2">Darija (Ar)</th></>}
                          <th className="text-right p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((rule, idx) => (
                          <tr key={rule.id || idx} className={isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-100'}>
                            {editingRule === rule.id ? (
                              <RuleEditRow
                                rule={rules.find(r => r.id === rule.id)}
                                ruleType={rulesSubTab}
                                isDarkMode={isDarkMode}
                                onSave={(updated) => { saveRule({ ...updated, id: rule.id }); }}
                                onCancel={() => setEditingRule(null)}
                              />
                            ) : (
                              <>
                                {rulesSubTab === 'conjugation' && (
                                  <>
                                    <td className={`p-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.tense}</td>
                                    <td className={`p-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.slot}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.prefix || '-'}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.ending || '-'}</td>
                                    <td className={`p-2 font-mono ${!rule.prefixAr && rule.prefix ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.prefixAr || '-'}</td>
                                    <td className={`p-2 font-mono ${!rule.endingAr && rule.ending ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.endingAr || '-'}</td>
                                  </>
                                )}
                                {rulesSubTab === 'verb_stem' && (
                                  <>
                                    <td className={`p-2 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.english}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.pastStem || '-'}</td>
                                    <td className={`p-2 font-mono ${rule.pastStem && !rule.pastStemAr ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.pastStemAr || (rule.pastStem ? 'missing' : '-')}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.presentStem || '-'}</td>
                                    <td className={`p-2 font-mono ${rule.presentStem && !rule.presentStemAr ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.presentStemAr || (rule.presentStem ? 'missing' : '-')}</td>
                                    <td className={`p-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{rule.phrasal ? 'Yes' : 'No'}</td>
                                  </>
                                )}
                                {rulesSubTab === 'context_rule' && (
                                  <>
                                    <td className={`p-2 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.verb}</td>
                                    <td className={`p-2 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>{rule.condition}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.use_verb}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.add_word || '-'}</td>
                                    <td className={`p-2 font-mono ${!rule.add_word_ar && rule.add_word ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.add_word_ar || (rule.add_word ? 'missing' : '-')}</td>
                                  </>
                                )}
                                {rulesSubTab === 'word_order' && (
                                  <>
                                    <td className={`p-2 font-mono font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.pattern}</td>
                                    <td className={`p-2 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.action}</td>
                                    <td className={`p-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{rule.description || '-'}</td>
                                  </>
                                )}
                                {rulesSubTab === 'sentence_pattern' && (
                                  <>
                                    <td className={`p-2 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.mode}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.pattern}</td>
                                    <td className={`p-2 font-mono ${!rule.patternAr ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.patternAr || 'missing'}</td>
                                  </>
                                )}
                                {rulesSubTab === 'place_word' && (
                                  <>
                                    <td className={`p-2 font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{rule.english}</td>
                                    <td className={`p-2 font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>{rule.darija}</td>
                                    <td className={`p-2 font-mono ${!rule.darijaAr ? (isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600') : (isDarkMode ? 'text-gray-300' : 'text-gray-600')}`} dir="rtl">{rule.darijaAr || 'missing'}</td>
                                  </>
                                )}
                                <td className="p-2 text-right">
                                  <button onClick={() => setEditingRule(rule.id)} className={`text-xs mr-2 ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}>Edit</button>
                                  <button onClick={() => deleteRule(rule.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Rule Form Component ─────────────────────────────────────────────────────

const RuleFormInput = ({ label, value, onChange, isDarkMode, dir, placeholder }) => (
  <div className="flex-1 min-w-[120px]">
    <label className={`text-[10px] font-bold block mb-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      dir={dir}
      placeholder={placeholder}
      className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
    />
  </div>
);

const RuleFormSelect = ({ label, value, onChange, options, isDarkMode }) => (
  <div className="flex-1 min-w-[120px]">
    <label className={`text-[10px] font-bold block mb-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</label>
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className={`w-full px-2 py-1 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const RuleForm = ({ rule, onChange, isDarkMode, ruleType }) => {
  const update = (field, value) => onChange({ ...rule, [field]: value });

  if (ruleType === 'conjugation') {
    return (
      <div className="flex flex-wrap gap-2">
        <RuleFormSelect label="Tense" value={rule.tense} onChange={v => update('tense', v)} isDarkMode={isDarkMode}
          options={[{value:'past',label:'Past'},{value:'present',label:'Present'},{value:'future',label:'Future'}]} />
        <RuleFormSelect label="Slot" value={rule.slot} onChange={v => update('slot', v)} isDarkMode={isDarkMode}
          options={[{value:'i',label:'I'},{value:'you_sm',label:'You (SM)'},{value:'you_sf',label:'You (SF)'},{value:'he',label:'He'},{value:'she',label:'She'},{value:'we',label:'We'},{value:'you_p',label:'You (P)'},{value:'they',label:'They'}]} />
        <RuleFormInput label="Prefix" value={rule.prefix} onChange={v => update('prefix', v)} isDarkMode={isDarkMode} />
        <RuleFormInput label="Ending" value={rule.ending} onChange={v => update('ending', v)} isDarkMode={isDarkMode} />
        <RuleFormInput label="Prefix (Ar)" value={rule.prefixAr} onChange={v => update('prefixAr', v)} isDarkMode={isDarkMode} dir="rtl" />
        <RuleFormInput label="Ending (Ar)" value={rule.endingAr} onChange={v => update('endingAr', v)} isDarkMode={isDarkMode} dir="rtl" />
      </div>
    );
  }

  if (ruleType === 'verb_stem') {
    return (
      <div className="flex flex-wrap gap-2">
        <RuleFormInput label="English Verb" value={rule.english} onChange={v => update('english', v)} isDarkMode={isDarkMode} placeholder="e.g. eat" />
        <RuleFormInput label="Past Stem" value={rule.pastStem} onChange={v => update('pastStem', v)} isDarkMode={isDarkMode} placeholder="e.g. kl" />
        <RuleFormInput label="Past Stem (Ar)" value={rule.pastStemAr} onChange={v => update('pastStemAr', v)} isDarkMode={isDarkMode} dir="rtl" />
        <RuleFormInput label="Present Stem" value={rule.presentStem} onChange={v => update('presentStem', v)} isDarkMode={isDarkMode} placeholder="e.g. kol" />
        <RuleFormInput label="Present Stem (Ar)" value={rule.presentStemAr} onChange={v => update('presentStemAr', v)} isDarkMode={isDarkMode} dir="rtl" />
        <RuleFormSelect label="Phrasal?" value={rule.phrasal ? 'true' : 'false'} onChange={v => update('phrasal', v === 'true')} isDarkMode={isDarkMode}
          options={[{value:'false',label:'No'},{value:'true',label:'Yes'}]} />
      </div>
    );
  }

  if (ruleType === 'context_rule') {
    return (
      <div className="flex flex-wrap gap-2">
        <RuleFormInput label="Verb (English)" value={rule.verb} onChange={v => update('verb', v)} isDarkMode={isDarkMode} placeholder="e.g. leave" />
        <RuleFormSelect label="Condition" value={rule.condition} onChange={v => update('condition', v)} isDarkMode={isDarkMode}
          options={[{value:'next_is_place',label:'Next is Place'},{value:'next_is_thing',label:'Next is Thing'},{value:'always',label:'Always'}]} />
        <RuleFormInput label="Use Verb" value={rule.use_verb} onChange={v => update('use_verb', v)} isDarkMode={isDarkMode} placeholder="e.g. get out" />
        <RuleFormInput label="Add Word" value={rule.add_word} onChange={v => update('add_word', v)} isDarkMode={isDarkMode} placeholder="e.g. mn" />
        <RuleFormInput label="Add Word (Ar)" value={rule.add_word_ar} onChange={v => update('add_word_ar', v)} isDarkMode={isDarkMode} dir="rtl" />
      </div>
    );
  }

  if (ruleType === 'word_order') {
    return (
      <div className="flex flex-wrap gap-2">
        <RuleFormInput label="Pattern" value={rule.pattern} onChange={v => update('pattern', v)} isDarkMode={isDarkMode} placeholder="e.g. ADJ+NOUN" />
        <RuleFormSelect label="Action" value={rule.action} onChange={v => update('action', v)} isDarkMode={isDarkMode}
          options={[{value:'flip',label:'Flip'},{value:'keep',label:'Keep'}]} />
        <RuleFormInput label="Description" value={rule.description} onChange={v => update('description', v)} isDarkMode={isDarkMode} />
      </div>
    );
  }

  if (ruleType === 'sentence_pattern') {
    return (
      <div className="flex flex-wrap gap-2">
        <RuleFormInput label="Mode" value={rule.mode} onChange={v => update('mode', v)} isDarkMode={isDarkMode} placeholder="e.g. negative" />
        <RuleFormInput label="Pattern" value={rule.pattern} onChange={v => update('pattern', v)} isDarkMode={isDarkMode} placeholder="e.g. ma + {verb} + sh" />
        <RuleFormInput label="Pattern (Ar)" value={rule.patternAr} onChange={v => update('patternAr', v)} isDarkMode={isDarkMode} dir="rtl" />
      </div>
    );
  }

  if (ruleType === 'place_word') {
    return (
      <div className="flex flex-wrap gap-2">
        <RuleFormInput label="English" value={rule.english} onChange={v => update('english', v)} isDarkMode={isDarkMode} placeholder="e.g. house" />
        <RuleFormInput label="Darija" value={rule.darija} onChange={v => update('darija', v)} isDarkMode={isDarkMode} placeholder="e.g. dar" />
        <RuleFormInput label="Darija (Ar)" value={rule.darijaAr} onChange={v => update('darijaAr', v)} isDarkMode={isDarkMode} dir="rtl" />
      </div>
    );
  }

  return null;
};

const RuleEditRow = ({ rule, ruleType, isDarkMode, onSave, onCancel }) => {
  const [edited, setEdited] = React.useState({ ...rule });
  const update = (field, value) => setEdited(prev => ({ ...prev, [field]: value }));

  const inputCls = `w-full px-1 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`;
  const selectCls = inputCls;

  const renderFields = () => {
    if (ruleType === 'conjugation') {
      return (
        <>
          <td className="p-1"><select value={edited.tense} onChange={e => update('tense', e.target.value)} className={selectCls}><option value="past">past</option><option value="present">present</option><option value="future">future</option></select></td>
          <td className="p-1"><select value={edited.slot} onChange={e => update('slot', e.target.value)} className={selectCls}><option value="i">i</option><option value="you_sm">you_sm</option><option value="you_sf">you_sf</option><option value="he">he</option><option value="she">she</option><option value="we">we</option><option value="you_p">you_p</option><option value="they">they</option></select></td>
          <td className="p-1"><input value={edited.prefix || ''} onChange={e => update('prefix', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.ending || ''} onChange={e => update('ending', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.prefixAr || ''} onChange={e => update('prefixAr', e.target.value)} className={inputCls} dir="rtl" /></td>
          <td className="p-1"><input value={edited.endingAr || ''} onChange={e => update('endingAr', e.target.value)} className={inputCls} dir="rtl" /></td>
        </>
      );
    }
    if (ruleType === 'verb_stem') {
      return (
        <>
          <td className="p-1"><input value={edited.english || ''} onChange={e => update('english', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.pastStem || ''} onChange={e => update('pastStem', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.pastStemAr || ''} onChange={e => update('pastStemAr', e.target.value)} className={inputCls} dir="rtl" /></td>
          <td className="p-1"><input value={edited.presentStem || ''} onChange={e => update('presentStem', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.presentStemAr || ''} onChange={e => update('presentStemAr', e.target.value)} className={inputCls} dir="rtl" /></td>
          <td className="p-1"><select value={edited.phrasal ? 'true' : 'false'} onChange={e => update('phrasal', e.target.value === 'true')} className={selectCls}><option value="false">No</option><option value="true">Yes</option></select></td>
        </>
      );
    }
    if (ruleType === 'context_rule') {
      return (
        <>
          <td className="p-1"><input value={edited.verb || ''} onChange={e => update('verb', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><select value={edited.condition} onChange={e => update('condition', e.target.value)} className={selectCls}><option value="next_is_place">next_is_place</option><option value="next_is_thing">next_is_thing</option><option value="always">always</option></select></td>
          <td className="p-1"><input value={edited.use_verb || ''} onChange={e => update('use_verb', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.add_word || ''} onChange={e => update('add_word', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.add_word_ar || ''} onChange={e => update('add_word_ar', e.target.value)} className={inputCls} dir="rtl" /></td>
        </>
      );
    }
    if (ruleType === 'word_order') {
      return (
        <>
          <td className="p-1"><input value={edited.pattern || ''} onChange={e => update('pattern', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><select value={edited.action} onChange={e => update('action', e.target.value)} className={selectCls}><option value="flip">flip</option><option value="keep">keep</option></select></td>
          <td className="p-1"><input value={edited.description || ''} onChange={e => update('description', e.target.value)} className={inputCls} /></td>
        </>
      );
    }
    if (ruleType === 'sentence_pattern') {
      return (
        <>
          <td className="p-1"><input value={edited.mode || ''} onChange={e => update('mode', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.pattern || ''} onChange={e => update('pattern', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.patternAr || ''} onChange={e => update('patternAr', e.target.value)} className={inputCls} dir="rtl" /></td>
        </>
      );
    }
    if (ruleType === 'place_word') {
      return (
        <>
          <td className="p-1"><input value={edited.english || ''} onChange={e => update('english', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.darija || ''} onChange={e => update('darija', e.target.value)} className={inputCls} /></td>
          <td className="p-1"><input value={edited.darijaAr || ''} onChange={e => update('darijaAr', e.target.value)} className={inputCls} dir="rtl" /></td>
        </>
      );
    }
    return null;
  };

  return (
    <>
      {renderFields()}
      <td className="p-1 text-right whitespace-nowrap">
        <button onClick={() => onSave(edited)} className="text-xs text-emerald-500 hover:text-emerald-700 mr-2">Save</button>
        <button onClick={onCancel} className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Cancel</button>
      </td>
    </>
  );
};

export default BrainAdminTab;
